import React, { useEffect, useReducer, useRef, useContext, useCallback } from 'react';
import { validateCart } from 'common/api/e-comm/cart';
import { useApi } from 'common/modules/async-actions/useApi';
import {
    FincenFlowState,
    IFincenFormUrl,
    defaultFincenFormUrl,
} from 'features/order-summary/components/fincen/types';
import { StatusCode } from 'common/api/config';
import { FincenContext, initialState, fincenState } from './context';
import { RequiresInformationSubmissionView } from './RequiresInformationSubmissionView';
import { RequiresInformationVerificationView } from './RequiresInformationVerificationView';
import { ChangingYourOrderView } from './ChangingYourOrderView';
import { useRootSelector } from 'common/features/featuresReducer';
import {
    OrderScreenContext,
    CheckoutFlowState,
} from 'features/order-summary/screens/CheckoutScreen';
import { Value } from 'firebase/remote-config';
import { isInIframe, getOrigin } from 'utils';

const _parseFincen = (value: Value) => {
    // CASE: values aren't loaded -- lets use the defaults which are empty strings
    return value.asString() ? JSON.parse(value.asString()) : defaultFincenFormUrl;
};

export const FincenFlowView = () => {
    const [validateCart_, , result] = useApi(validateCart);
    const [state, dispatch] = useReducer(fincenState, initialState);
    const cart = useRootSelector((s) => s.store.cart.cart);
    const lastUpdatedTime = useRef<string | undefined>();
    const { setCheckoutFlowState, showFincen, setShowFincen } = useContext(OrderScreenContext);
    const fincen = {
        updateFincenInfoUrl: `${getOrigin()}/${process.env.REACT_APP_UPDATE_FINCEN_INFO_URL}`,
        enterFincenInfoUrl: `${getOrigin()}/${process.env.REACT_APP_ENTER_FINCEN_INFO_URL}`,
        moreInfoUrl: `${getOrigin()}/${process.env.REACT_APP_FINCEN_MORE_INFO_URL}`,
    } as IFincenFormUrl;

    // Side-Effect(A): validate the cart whenever it is updated.
    // We utilize `updatedTime` instead of `cart` to avoid many api-calls
    // to `validateCart`.
    useEffect(() => {
        if (lastUpdatedTime.current != cart?.updatedTime) {
            validateCart_();
        }
    }, [cart?.updatedTime, validateCart_]);

    // Side-Effect(B): if cart changes we want to update our lastUpdatedTime reference
    // to update to the latest updatedTime.
    useEffect(() => {
        lastUpdatedTime.current = cart?.updatedTime;
    }, [cart]);

    // Side-Effect(C): Enable/Disable the cart-checkout button
    // depending on the FinCEN flow state.
    // - CAN_ORDER will tell the checkout to render the `Checkout` buttons
    //    without the __disabled__ state.
    // - Any other state will __disable__ the `Checkout` buttons,
    // not allowing the user to continue forward. These buttons are any buttons
    // that allow you to advance through the checkout flow.
    useEffect(() => {
        if (state.fincenFlowState === FincenFlowState.CAN_ORDER) {
            setCheckoutFlowState(CheckoutFlowState.ENABLED);
            setShowFincen(false);
        } else {
            setCheckoutFlowState(CheckoutFlowState.DISABLED);
        }
    }, [state.fincenFlowState, showFincen, setCheckoutFlowState, setShowFincen]);

    const processValidateCartResult = useCallback(
        (result: Awaited<ReturnType<typeof validateCart>>) => {
            if (result) {
                const fincen =
                    result.data?.fincenResponse || result?.error?.body?.fincenResponse || null;
                const shouldRunFincenFlow =
                    result.response?.status === StatusCode.UnprocessableEntity;
                dispatch({
                    name: 'SET_FINCEN_DATA',
                    payload: {
                        fincen,
                        shouldRunFincenFlow,
                    },
                });
            }
        },
        [dispatch]
    );

    useEffect(() => {
        processValidateCartResult(result!);
    }, [result, processValidateCartResult]);

    const renderFincenView = () => {
        if (isInIframe()) {
            // return because we only want fincen to show up in the main window since it handles navigation to the fincen site
            return null;
        }
        switch (state.fincenFlowState) {
            case FincenFlowState.CAN_ORDER:
                return null;
            case FincenFlowState.REQUIRES_INFORMATION_VERIFICATION:
                return <RequiresInformationVerificationView />;
            case FincenFlowState.REQUIRES_INFORMATION_SUBMISSION:
                return <RequiresInformationSubmissionView />;
            case FincenFlowState.WILL_EDIT_CART:
                return <ChangingYourOrderView />;
        }
    };

    const context = {
        data: state,
        dispatch,
        enterInfoUrl: fincen?.enterFincenInfoUrl || '',
        editInfoUrl: fincen?.updateFincenInfoUrl || '',
        moreInfoUrl: fincen?.moreInfoUrl || '',
        processValidateCartResult,
    };
    return <FincenContext.Provider value={context}>{renderFincenView()}</FincenContext.Provider>;
};
